import { User } from 'src/store/models/index.js'
import Users from 'src/api/users.js'
import sentryConfig from 'src/store/sentry.js'
import tracking, { globalTracking } from 'src/config/tracking/index.js'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'
import Auth from 'src/api/auth.js'
import Core from 'src/api/core.js'
import native from 'src/config/native.js'
import GlobalPrototypes from 'src/config/services/globalPrototypes.ts'
import { resetStoreAutoDispatcher } from 'src/store/autoDispatcher.js'
import BroadcastingServiceProvider from 'src/config/services/broadcasting/BroadcastingServiceProvider.ts'
import { startGlobalBroadcastingListeners, stopGlobalBroadcastingListeners } from 'src/config/services/broadcasting/handlers/global.ts'
import { registerServiceWorker, unregisterServiceWorker } from 'src/config/setup/registerServiceWorker.ts'
import { setFirstTargetRouteAfterAppStart } from 'src/router/routerUtils.js'
import { KeycloakAuth } from 'src/config/auth/KeycloakAuth.ts'
import { requiresOidcAuth } from '@/composables/auth/useAuthHelper.ts'
import { AUTH_TYPE } from 'src/config/constants.ts'

let isInitDispatcherRunning = false


const defaultState = () => ({
  me_id: null,
  loading_self: false,
  is_invalid_user: false
})

export const state = defaultState()

export const getters = {
  me(state) {
    return User.query()
      .where('id', state.me_id)
      .with('childs.grade|school')
      .with('parents.grade|school')
      .with('grade')
      .with('school')
      .first() // || new User()
  },
  childToRegister(state, getters) {
    if (!getters.me || !getters.me.childs) return
    return getters.me.childs.find(child => child.can.register)
  },
  stored_user_ids() {
    return User.query()
      .all()
      .map(user => ({
        id: user.id,
        uuid: user.uuid
      }))
  },
  getIsUserInValid(state) {
    return state.is_invalid_user
  },
  getLoadingSelf(state) {
    return state.loading_self
  }
}

export const mutations = {
  RESET_STATE(state) {
    Object.assign(state, defaultState())
  },
  RESET_LOCAL_STORAGE() {
    // TODO: dont remove slink
    localStorage?.removeItem('slink')
    localStorage?.removeItem('auth_token')
    localStorage?.removeItem('multiple_users')
    localStorage?.removeItem('keep_alive')
    window.sessionStorage?.removeItem('auth_token')
  },
  ME_ID(state, id) {
    state.me_id = id
  },
  LOADING_SELF(state, bool) {
    state.loading_self = bool
  },
  SET_IS_INVALID_USER(state, bool) {
    state.is_invalid_user = bool
  }
}

const isSsoCallbackWithExistingUser = () => {
  return GlobalPrototypes.router.resolve(window.location.pathname).name === 'sso-verify' && !!localStorage.getItem('auth_token')
}

export const actions = {
  // Method will be executed before the app is initialized (see main.ts)
  async init({ dispatch, getters, rootGetters }) {

    // Check if there is a stored realm from a previous OIDC session.
    const currentStoredUser = rootGetters['multiUsers/currentStoredUser']
    const storedRealm = await KeycloakAuth.getStoredRealm()
      ?? (currentStoredUser && requiresOidcAuth(currentStoredUser) ? currentStoredUser.realm : undefined)

    // Initialize KeycloakAuth from local storage (handles page refresh)
    if (storedRealm) {
      await KeycloakAuth.getInstance(storedRealm)
    }

    if (getters.me) return

    // Skip getSelf on SSO verify route to avoid API calls with the previous user's (possibly expired) token.
    // The new user's data will be loaded after loginIdm/parseLoginData completes.
    if (isSsoCallbackWithExistingUser()) return

    isInitDispatcherRunning = true
    await dispatch('getSelf')
    isInitDispatcherRunning = false
  },

  // Method will be executed after the app is initialized and all init methods of store modules are finished (see router.js)
  async afterInit({ dispatch, getters }) {
    if (!getters.me) return
    await dispatch('getActivitySummary')
  },

  async login({ dispatch, rootState }, data) {
    const response = await Auth.postAuthLogin(data, {
      showMessages: data.showError,
      headers: {
        Authorization: null
      }
    }, rootState.isNative)

    return dispatch('parseLoginData', { response, stayLoggedIn: data.stayLoggedIn, preventAutomaticRedirect: data.preventAutomaticRedirect, track: data.track })
  },

  async loginIdm({ dispatch, rootState }, data) {
    const response = await Auth.postAuthLoginIdm(data, {
      showMessages: data.showError,
      headers: {
        Authorization: null
      }
    }, rootState.isNative)
    return dispatch('parseLoginData', { response, stayLoggedIn: data.stayLoggedIn, preventAutomaticRedirect: data.preventAutomaticRedirect, track: data.track })
  },

  async parseLoginData({ commit, dispatch, rootState }, { response, stayLoggedIn, preventAutomaticRedirect, track, oidcUser, realm }) {
    const school = rootState.school
    // TODO: temporary change to show passport login input along with sdui SSO button. To be removed once we SSO is completed
    const isSingleSignOnActivated = !!school?.meta?.group_iam?.length && localStorage?.getItem('kc-login-in-progress') === 'true'
    if (!isSingleSignOnActivated && response.data?.tfa_mode === 'email') return response

    // If we got redirected from SSO provider, and we already have a user connected and stored in cache, save them to
    // localStorage "multiple_users" first (to not lose them), before we clear the caches
    if (isSsoCallbackWithExistingUser()) {
      const cacheSelf = await Users.getSelfUser()
      const { data } = cacheSelf  // no network call, just getting cached data
      await dispatch('multiUsers/saveUser', { user: data }, { root: true }) // save existing user to localStorage
    }

    // Reset LocalStorage as it will be rebuilt for the current user
    await dispatch('resetLocalStorage')

    // Clear cache storage as it will be rebuilt for the current user
    await dispatch('resetCacheStorage')

    const authToken = response.data.access_token

    commit('SET_AUTH_TOKEN', { stayLoggedIn, authToken }, { root: true })
    commit('SET_STAY_LOGGED_IN', stayLoggedIn, { root: true })

    dispatch('keepAlive/setKeepAlivePing', null, { root: true })

    // We append OIDC data to the getSelf dispatch,
    // So we can handle user creation / selection for OIDC users.
    const res = await dispatch('getSelf', {
      oidcUser,
      realm,
      authType: oidcUser ? AUTH_TYPE.KEYCLOAK : AUTH_TYPE.PASSPORT
    })
    track && track()

    setTimeout(() => dispatch('savePushToken', {}, { root: true }), 10 * 1000)

    // for ios / android push notification
    native.registerPushNotification()

    // reset login value
    commit('multiUsers/CHANGE_USE_LOGIN', false, { root: true })

    const user = res?.data
    localStorage?.setItem('slink', user?.school?.slink)
    localStorage?.removeItem('kc-login-in-progress')
    // navigate to start page if preventAutomaticRedirect is not set to false
    // We need to prevent this redirection in case of Onboarding, where the wizard wrappers take care of redirecting themselves.
    // (e.g. Parent registration redirects to child registration)
    if (preventAutomaticRedirect) return response

    // Part of "afterInit", but this only gets called when app is started. Also need to call it
    // after logging in (app has already started)
    await dispatch('getActivitySummary')

    await dispatch('multiUsers/selectUser', { userId: user?.id, path: GlobalPrototypes.router.resolve(GlobalPrototypes.defaultRoute()).href }, { root: true })

    // make sure branding is shown
    user?.school && GlobalPrototypes.store.dispatch('schoolBranding/applyBranding', user?.school)

    return response
  },

  async logoutRequest() {
    const { data } = await Core.postLogout({ device_token: GlobalPrototypes.store.state.device.firebaseToken || undefined })
    return data
  },

  async logoutClearCache({ commit, dispatch, rootState }, user) {
    // backup the slink if available, so we can redirect to the schools login page
    const bakSlink = rootState.slink || localStorage?.getItem('slink')

    // A lot of computed/reactive logic relies on $me and other global state variables, which need to be reset when user logs out.
    // Route to loading screen before resetting data to prevent features from turning to error state.
    await GlobalPrototypes.router.replace({ name: 'loading', query: { logout: 'true' } })

    if (user?.redirect_uri) {
      native.openUrlInAppBrowser(user.redirect_uri)
    }

    // Route back to login page or '/', if slink is not available
    const slink = user?.school?.slink || rootState.slink || localStorage?.getItem('slink')
    const loginPath = slink ? GlobalPrototypes.router.resolve({ name: 'login-sign-in', params: { slink } }).href : '/'

    // Disconnect global broadcasting listeners + stop broadcasting service
    stopGlobalBroadcastingListeners(user?.id)
    BroadcastingServiceProvider.stopCurrent()

    // Unregister service worker
    unregisterServiceWorker()

    // Reset CacheStorage + LocalStorage + OIDC Storage
    await dispatch('resetCacheStorage')
    await dispatch('resetLocalStorage')
    await dispatch('resetOidcStorage')

    // Reset keep alive interval if it's running
    commit('keepAlive/CLEAR_INTERVAL', null, { root: true })

    // Reset store state + VueX-ORM modules
    await resetStoreAutoDispatcher(GlobalPrototypes.store)

    // Since store.state was cleared, re-set states for native checks such as isNative, isAndroid, isIOS. Those
    // are required for the login page.
    native.initNativeStoreState(GlobalPrototypes.store)

    // Set slink again if available enabling us to redirect to schools login page instead of welcome page
    bakSlink && await dispatch('SET_SLINK', bakSlink, { root: true })

    // Auto logout was decided during init dispatching (getSelf). To avoid showing error states, we change the first
    // route target to the login page that will be picked up after app mounting is done.
    if (isInitDispatcherRunning) return setFirstTargetRouteAfterAppStart({ path: loginPath })

    // Skip final redirect for OIDC logout - Keycloak will handle the redirect
    if (user?.skipFinalRedirect) return

    await GlobalPrototypes.router.replace(loginPath)
  },

  async logout({ dispatch, rootState }, object) {
    const kcAuth = await KeycloakAuth.getInstance(object?.realm)
    const oidcUser = await kcAuth?.getUser()
    const idToken = object?.idToken || oidcUser?.id_token

    // OIDC logout
    if (idToken && kcAuth) {
      const slink = object?.school?.slink || rootState.slink || localStorage?.getItem('slink')

      await dispatch('logoutClearCache', { ...object, skipFinalRedirect: true })
      await kcAuth.logout({ idToken, slink })
      return
    }

    // Passport logout
    const { redirect_uri } = await dispatch('logoutRequest')
    await dispatch('logoutClearCache', { ...object, redirect_uri })
  },

  async autoLogout({ getters, dispatch }, requestUser) {
    const targetUser = requestUser && Object.keys(requestUser).length ? requestUser : null
    const me = getters.me || targetUser || { id: null }

    // remove push token for the user that is getting logged out for the current device
    // so that they do not receive push notifications anymore for that device
    if (GlobalPrototypes.store.state.device.firebaseToken && me.id) {
      await Users.deleteUserPushToken(me.id, GlobalPrototypes.store.state.device.firebaseToken)
    }

    clyTracking.trackAuth().action.logout({ origin: 'system' })
    // don´t need to make logout-request because refresh/access_token are invalid anyways
    await dispatch('logoutClearCache', me)
  },

  async resolveOidcToken({ dispatch }, kcAuth) {
    sentryConfig.addBreadcrumb({
      category: 'auth/refreshToken OIDC',
      message: 'attempting OIDC silent refresh',
      level: 'info'
    })

    const oidcUser = await kcAuth.getUser()
    if (oidcUser) {
      const newUser = await kcAuth.signinSilent()
      if (newUser?.access_token) return newUser.access_token
    }

    await dispatch('autoLogout')
    throw new Error('OIDC token refresh failed')
  },

  async refreshPassportToken(_, { user, isInactiveUser, rootState }) {
    const res = await Auth.postAuthRefresh(
      {
        uuid: user.uuid
      },
      {
        timeout: 10 * 60 * 1000, // set timeout to high
        actingAs: isInactiveUser && user
      }, rootState.isNative)

    return res.data.access_token
  },

  async refreshToken({ rootState, commit, dispatch }, { user }) {
    // If the user is inactive
    // we still want to attempt to refresh their token (to keep their session alive in case they switch back), but we want to be careful to not override the active user's token or session data. We use the isInactiveUser flag to determine if the user being refreshed is the currently active user or not, and handle the token refresh accordingly.
    const isInactiveUser = user.uuid !== localStorage?.getItem('user_uuid')

    sentryConfig.addBreadcrumb({
      category: 'auth/refreshToken start',
      message: `refresh started (inactive: ${isInactiveUser})`,
      level: 'warning'
    })

    const kcAuth = await KeycloakAuth.getInstance()

    // If there isn't any kcAuth instance, we fallback to refreshing the token via Passport.
    const authToken = kcAuth
      ? await dispatch('resolveOidcToken', kcAuth)
      : await dispatch('refreshPassportToken', { user, isInactiveUser, rootState })

    // If the refresh happened for a user which isn't active right now, don't set the auth token as global axios header
    // this situation can happen if you log out any other users than the active one in "/users-select"
    if (!isInactiveUser) {
      commit('SET_AUTH_TOKEN', {
        stayLoggedIn: rootState.stayLoggedIn,
        authToken
      }, { root: true })

      commit('multiUsers/SAVE_USER_AUTH_TOKEN', { userId: user.id, authToken }, { root: true })

      // Stop and restart broadcasting service with new auth token. This will also re-bind all previous event listeners.
      BroadcastingServiceProvider.getService()?.restart(authToken)
    }

    return { authToken }
  },

  async refreshCurrentLanguage({ getters }) {
    const me = getters.me

    // load current browser language
    if (!me) return GlobalPrototypes.switchLocale()

    await GlobalPrototypes.switchLocale(me.locale)
  },

  async getSelf({ commit, state, dispatch, getters }, { useCache = true, oidcUser, realm, authType } = {}) {
    const authToken = await dispatch('loadAuthToken', null, { root: true })
    const isMonitor = window.location.pathname.includes('/monitor/')
    if (isMonitor || !authToken) {
      // Register SW for monitor
      isMonitor && registerServiceWorker()
      return
    }

    commit('LOADING_SELF', true)

    const saveSelf = async res => {
      const meId = res.data.id
      const school = res.data?.school
      const meAlreadyExists = state.me_id === meId

      commit('ME_ID', meId)
      if (school) {
        GlobalPrototypes.store.commit('schoolBranding/SET_BRANDING', school)
        // We store slink and school when logging in but also need to store on page reload
        !GlobalPrototypes.store.state.slink && GlobalPrototypes.store.commit('SET_SLINK', school.slink)
        !GlobalPrototypes.store.state.school && GlobalPrototypes.store.commit('SET_SCHOOL', school)
      }
      await User.insert(GlobalPrototypes.clone({ data: res.data }))

      const isTrackable = res.data.is_trackable

      if (isTrackable && (import.meta.env.VITE_SENTRY_ACTIVATED === 'true')) {
        sentryConfig.followUser(meId, school?.slink)
      }

      const me = getters.me

      startTracking(me, isTrackable)

      !isTrackable && tracking.stopTracking()

      // set user uuid
      commit('SET_USER_UUID', me.uuid, { root: true })

      /* localize functions */
      await dispatch('refreshCurrentLanguage')

      // check and redirect user to onboarding
      checkOnboarding(me)

      // only save push token, on first user load
      if (meAlreadyExists) return

      GlobalPrototypes.store.dispatch('savePushToken')
    }

    const startTracking = (me, isTrackable) => {
      if (!isTrackable || !me) return
      const countlyUser = {
        id: me.id,
        organization: me.school.slink,
        custom: {
          user_type: me.type,
          school_type: me.school.type.name,
          school_level: me.school.type.level,
          language: me.locale,
          branding: me.school.app_branding,
          permissions: Object.entries(me.can).filter(([_, value]) => value || value === 1).map(([key]) => key),
          roles: me.roles.map(obj => obj.name),
          registrationDate: me.registered_at
        }
      }
      tracking.init(countlyUser)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { permissions, roles, user_type, ...rest } = countlyUser.custom
      globalTracking.init({ uuid: me.school.uuid, organization: me.school.slink, custom: { ...rest } })
    }

    // check if user should be redirected to onBoarding
    const checkOnboarding = (me) => {
      if (window.location.href.includes('onboarding') || me.school.state !== 'ONBOARDING') return

      const onboardingStartRoute = { name: GlobalPrototypes.onboardingDefaultRoute(), params: { schoolUuid: me.school.uuid } }
      if (isInitDispatcherRunning) return setFirstTargetRouteAfterAppStart(onboardingStartRoute)
      GlobalPrototypes.router.push(onboardingStartRoute)
    }

    const refreshSelfCache = async (networkRequest) => {
      const res = await networkRequest()
      await saveSelf(res)
    }

    try {
      // load from cache first and refresh /self in "background"
      const cacheSelf = await Users.getSelfUser()
      const { data, network } = cacheSelf

      // save cached data
      useCache && await saveSelf({ data })

      // refresh /self data: if we use cache -> no await to not block UI
      if (useCache) {
        refreshSelfCache(network)
      } else {
        await refreshSelfCache(network)
      }

      await dispatch('multiUsers/saveUser', {
        user: data,
        authType,
        realm,
        keycloakSub: oidcUser?.profile?.sub
      }, { root: true }) // await, in case user is switching
      await dispatch('getFamily')
      await dispatch('keepAlive/initInterval', null, { root: true })

      getters.me?.school?.broadcastingService?.start(authToken)
      // Connect global broadcasting listener
      startGlobalBroadcastingListeners(state.me_id)

      // Enable offline support (runtime cache) + fast asset loading (pre-cache)
      registerServiceWorker()

      commit('LOADING_SELF', false)

      return { data }
    } catch (e) {
      commit('LOADING_SELF', false)
      // Ignore 401 errors of self call, axios interceptor will handle it by attempting to refresh the auth token
      if (e?.response?.status && e.response.status !== 401) throw (e)
    }
  },

  async getFamily({ state, getters, dispatch }) {
    const res = await Users.getSelfFamily()
    await User.insert({ data: res.data })

    if (getters.childToRegister) {
      dispatch('startChildRegistration')
    }

    const childs = res.data.filter(el => el.type === 'STUDENT')
    await dispatch('multiUsers/saveFamily', { childs, userId: state.me_id }, { root: true })
    return res
  },

  resetLocalStorage() {
    localStorage?.removeItem('slink')
    localStorage?.removeItem('auth_token')
    localStorage?.removeItem('multiple_users')
    localStorage?.removeItem('keep_alive')
    window.sessionStorage?.removeItem('auth_token')
  },

  async resetOidcStorage() {
    await KeycloakAuth.clearStorage()
  },

  async resetCacheStorage() {
    if (typeof caches === 'undefined' || !caches) return
    try {
      const keys = await caches.keys()
      if (!keys || !keys.length) return

      // these 'static' caches will be reset by service-worker if needed
      const staticCaches = ['sdui-precache', 'workbox-precache', 'static_chunks']
      const dynamicCachesToDelete = keys.filter(key => !staticCaches.find(staticCache => key.includes(staticCache)))

      const promises = dynamicCachesToDelete.map(key => caches.delete(key))
      await Promise.all(promises)
    } catch (e) {
      sentryConfig.throwError(e)
    }
  },

  // to keep parent with unregistered childs on the child-registration screens
  startChildRegistration() {
    if (
      GlobalPrototypes.router.currentRoute.value &&
      GlobalPrototypes.router.currentRoute.value.meta &&
      GlobalPrototypes.router.currentRoute.value.meta.interceptChildRegistration
    ) return

    GlobalPrototypes.router.beforeEach((to, from, next) => {
      const childToRegister = GlobalPrototypes.store.getters['users/childToRegister']
      if (!childToRegister) return next()
      if (to.name.includes('add-child')) return next()
      next({ name: 'add-child-activation-key' })
    })

    GlobalPrototypes.router.push({ name: 'add-child-activation-key' })
  },

  async getActivitySummary() {
    const res = await Users.getActivitySummary()

    if (!res) return

    // if there is data.notification, we know that feature flag for NC is activated
    const notificationsUnreadCount = res.data.notification?.unread_count
    await GlobalPrototypes.store.dispatch('notification/updateUnreadNotificationCount', notificationsUnreadCount || 0)

    const chatMessagesUnreadCount = res.data.chat?.unread_count
    await GlobalPrototypes.store.dispatch('channel/updateUnreadCount', chatMessagesUnreadCount || 0)
  }
}
